import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useRestoreBreakdownVersion } from '@/hooks/useBreakdown';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface RestoreConfirmDialogProps {
  jobId: string;
  versionId: string;
  versionName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  hasUnsavedChanges?: boolean;
}

export function RestoreConfirmDialog({
  jobId,
  versionId,
  versionName,
  open,
  onOpenChange,
  onSuccess,
  hasUnsavedChanges = false,
}: RestoreConfirmDialogProps) {
  const restoreVersion = useRestoreBreakdownVersion(jobId);

  const handleRestore = async () => {
    restoreVersion.mutate(versionId, {
      onSuccess: () => {
        onOpenChange(false);
        onSuccess();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Restore Snapshot
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to restore &ldquo;{versionName}&rdquo;?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-gray-600">
            This will replace your current breakdown with the data from this saved snapshot.
            {hasUnsavedChanges && (
              <span className="block mt-2 text-amber-600 font-medium">
                Warning: You have unsaved changes that will be lost.
              </span>
            )}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={restoreVersion.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRestore}
            disabled={restoreVersion.isPending}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {restoreVersion.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Restoring...
              </>
            ) : (
              'Restore Snapshot'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
